import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import AppShell from "@/components/AppShell";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Calendar, X, Link as LinkIcon, Send, Mail, Sparkles } from "lucide-react";
import { toast } from "sonner";
import SmsSettingsCard from "@/components/SmsSettingsCard";

export default function Settings() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [gcal, setGcal] = useState(null);
  const [prefs, setPrefs] = useState(null);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    api.get("/oauth/google-calendar/status").then((r) => setGcal(r.data)).catch(() => setGcal({ configured: false, connected: false }));
    api.get("/me/preferences").then((r) => setPrefs(r.data)).catch(() => setPrefs({ digest_opt_out: false }));
    if (params.get("gcal") === "connected") toast.success("Google Calendar connected — try the test event!");
  }, [params]);

  const connectGcal = async () => {
    try {
      const { data } = await api.get(`/oauth/google-calendar/start?app_origin=${encodeURIComponent(window.location.origin)}`);
      window.location.href = data.authorization_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Calendar connection not available yet");
    }
  };

  const disconnectGcal = async () => {
    try {
      await api.post("/oauth/google-calendar/disconnect");
      toast.success("Google Calendar disconnected");
      setGcal((g) => ({ ...g, connected: false }));
    } catch { toast.error("Could not disconnect"); }
  };

  const sendTestEvent = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/oauth/google-calendar/test-event");
      toast.success("Test event added to your Google Calendar");
      if (data.html_link) window.open(data.html_link, "_blank");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not push test event");
    } finally { setTesting(false); }
  };

  const togglePref = async (field, opt_out_value, label) => {
    setPrefs((p) => ({ ...p, [field]: opt_out_value }));
    try {
      await api.put("/me/preferences", { [field]: opt_out_value });
      toast.success(opt_out_value ? `${label} paused` : `${label} enabled`);
    } catch {
      toast.error("Could not save preference");
      setPrefs((p) => ({ ...p, [field]: !opt_out_value }));
    }
  };

  return (
    <AppShell>
      <div className="max-w-3xl mx-auto px-6 py-12">
        <h1 className="font-display text-3xl md:text-4xl font-semibold">Settings</h1>
        <p className="text-[#7A736E] mt-2">Manage your account, integrations, and membership.</p>

        <div className="hc-card p-6 mt-8" data-testid="settings-profile">
          <h2 className="font-display text-xl mb-4">Profile</h2>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div><p className="text-xs uppercase tracking-wider text-[#7A736E]">Name</p><p>{user?.name}</p></div>
            <div><p className="text-xs uppercase tracking-wider text-[#7A736E]">Email</p><p>{user?.email}</p></div>
            <div><p className="text-xs uppercase tracking-wider text-[#7A736E]">Auth provider</p><p className="capitalize">{user?.auth_provider}</p></div>
            <div><p className="text-xs uppercase tracking-wider text-[#7A736E]">Membership</p>
              <p className="capitalize flex items-center gap-2">
                {user?.subscription_tier === "paid" ? "Premium (Hope Chest+)" : "Free"}
                {user?.founding_member && (
                  <span
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-gradient-to-r from-[#3A5A4A] to-[#2C4538] text-[#E8DCC8] font-medium"
                    title={user?.founding_member_at ? `Founding Member since ${new Date(user.founding_member_at).toLocaleDateString()}` : ""}
                    data-testid="settings-founding-badge"
                  >
                    <Sparkles className="w-3 h-3" />
                    Founder #{user.founding_member_number}
                  </span>
                )}
              </p>
            </div>
          </div>
        </div>

        <div className="hc-card p-6 mt-6" data-testid="settings-notifications">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-2xl bg-[#E8DCC8] flex items-center justify-center">
              <Mail className="w-5 h-5 text-[#3A5A4A]" />
            </div>
            <div className="flex-1">
              <h2 className="font-display text-xl">Notifications</h2>
              <p className="text-xs text-[#7A736E] mt-1 mb-3">All emails are gentle, low-frequency, and skip days when there's nothing to share.</p>

              <div className="mt-4 flex items-center justify-between gap-4 py-3 border-t border-[#EBE5DF]">
                <div>
                  <Label htmlFor="digest-toggle" className="text-sm font-medium cursor-pointer">Daily digest (7:00 AM UTC)</Label>
                  <p className="text-xs text-[#7A736E] mt-1">Morning summary of today's appointments, tasks, and medications.</p>
                </div>
                {prefs && (
                  <Switch id="digest-toggle" checked={!prefs.digest_opt_out} onCheckedChange={(c) => togglePref("digest_opt_out", !c, "Daily digest")} data-testid="digest-toggle" />
                )}
              </div>

              <div className="flex items-center justify-between gap-4 py-3 border-t border-[#EBE5DF]">
                <div>
                  <Label htmlFor="weekly-toggle" className="text-sm font-medium cursor-pointer">Weekly care-team report (Sundays)</Label>
                  <p className="text-xs text-[#7A736E] mt-1">A summary of the past week, sent to the whole care team.</p>
                </div>
                {prefs && (
                  <Switch id="weekly-toggle" checked={!prefs.weekly_report_opt_out} onCheckedChange={(c) => togglePref("weekly_report_opt_out", !c, "Weekly report")} data-testid="weekly-toggle" />
                )}
              </div>

              <div className="flex items-center justify-between gap-4 py-3 border-t border-[#EBE5DF]">
                <div>
                  <Label htmlFor="nudge-toggle" className="text-sm font-medium cursor-pointer">Gentle check-in nudges</Label>
                  <p className="text-xs text-[#7A736E] mt-1">A soft reminder if a Hope Chest has gone 48 hours without any updates.</p>
                </div>
                {prefs && (
                  <Switch id="nudge-toggle" checked={!prefs.nudge_opt_out} onCheckedChange={(c) => togglePref("nudge_opt_out", !c, "Check-in nudges")} data-testid="nudge-toggle" />
                )}
              </div>
            </div>
          </div>
        </div>

        <SmsSettingsCard user={user} />

        <div className="hc-card p-6 mt-6" data-testid="settings-gcal">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-2xl bg-[#D8E2D8] flex items-center justify-center flex-shrink-0">
                <Calendar className="w-5 h-5 text-[#3A5A4A]" />
              </div>
              <div>
                <h2 className="font-display text-xl">Google Calendar</h2>
                <p className="text-sm text-[#7A736E] mt-1">
                  {gcal?.connected
                    ? "Connected — Hope Planner events push to your Google Calendar automatically."
                    : gcal?.configured
                    ? "Push appointments and planner events straight to your personal Google Calendar."
                    : "Calendar push isn't configured on this server yet."}
                </p>
                {gcal?.connected && (
                  <div className="mt-3">
                    <Button onClick={sendTestEvent} disabled={testing} variant="outline" size="sm" className="rounded-full border-[#EBE5DF]" data-testid="gcal-test-event">
                      <Send className="w-3.5 h-3.5 mr-2" /> {testing ? "Sending…" : "Send test event"}
                    </Button>
                  </div>
                )}
                {!gcal?.connected && gcal?.configured && (
                  <div className="mt-3 px-3 py-2.5 rounded-xl bg-[#F3EFEA] border border-[#EBE5DF] text-xs text-[#7A736E] leading-relaxed">
                    <p className="font-medium text-[#3D3935] mb-1">⚠️ Heads up: Google will show a warning screen</p>
                    <p>
                      Google will say <em>"Google hasn't verified this app"</em> before connecting. That's expected — Hope Chest is still in early access. Click <strong>"Advanced"</strong> → <strong>"Go to Hope Chest (unsafe)"</strong> to continue. Your data is safe; only Google's automated verification is pending.
                    </p>
                  </div>
                )}
              </div>
            </div>
            {gcal?.connected ? (
              <Button onClick={disconnectGcal} variant="outline" className="rounded-full border-[#EBE5DF]" data-testid="gcal-disconnect">
                <X className="w-4 h-4 mr-2" /> Disconnect
              </Button>
            ) : (
              <Button onClick={connectGcal} disabled={!gcal?.configured} className="rounded-full bg-[#3A5A4A] hover:bg-[#2C4538] disabled:opacity-50" data-testid="gcal-connect">
                <LinkIcon className="w-4 h-4 mr-2" /> {gcal?.configured ? "Connect Google Calendar" : "Not configured"}
              </Button>
            )}
          </div>
        </div>

        <div className="hc-card p-6 mt-6" data-testid="settings-membership">
          <h2 className="font-display text-xl mb-2">Membership & billing</h2>
          <p className="text-sm text-[#7A736E] mb-4">
            {user?.subscription_tier === "paid"
              ? "You're a Hope Chest+ member. View payment history or cancel anytime."
              : "Upgrade to Hope Chest+ for unlimited Hope Chests, family collaboration, and advanced reports."}
          </p>
          <div className="flex gap-2 flex-wrap">
            <Button onClick={() => nav("/billing")} className="rounded-full bg-[#3A5A4A] hover:bg-[#2C4538]" data-testid="settings-billing-button">
              Manage billing
            </Button>
            {user?.subscription_tier !== "paid" && (
              <Button onClick={() => nav("/pricing")} variant="outline" className="rounded-full border-[#EBE5DF]" data-testid="settings-pricing-button">
                See pricing
              </Button>
            )}
          </div>
        </div>

        <div className="hc-card p-6 mt-6">
          <h2 className="font-display text-xl mb-2">Sign out</h2>
          <p className="text-sm text-[#7A736E] mb-4">End your current session on this device.</p>
          <Button variant="outline" onClick={async () => { await logout(); nav("/"); }} className="rounded-full border-[#EBE5DF]" data-testid="settings-logout-button">
            Sign out
          </Button>
        </div>
      </div>
    </AppShell>
  );
}
